import { LitElement, html } from "lit-element";
import { barcode as barcode_svg, close, done, flashOff, flashOn } from "../styles/icons.js";
import { colorTheme } from "../styles/color-theme.js";
import { InspxDialog } from "../elements/inspx-dialog.js";
import { SharedStyles } from "../styles/shared-styles.js";
import { BarcodeFormat, BrowserMultiFormatReader, DecodeHintType, NotFoundException } from '@zxing/library/esm';
import { NativeBarcodeScanner } from '../lib/native-barcode-scanner.js';
import './ddl-input.js';

class BarcodeScanner extends LitElement {

	constructor() {
		super();

		this.__keyDown = this._keyDown.bind(this);
		this.results = [];
		this.defaultResult = 'not recognized yet'
		this.prevBarcode = this.defaultResult;

		let allFormats = [{ val: '-1', text: 'All possible formats' }];
		this.isNativeSupported = NativeBarcodeScanner.isNativeBarcodeScannerSupported;
		if (this.isNativeSupported) {
			NativeBarcodeScanner.getSupportedFormats().then(nativeFormats => {
				this._allNativeFormats = nativeFormats;
				this.barcodeFormats = allFormats.concat(nativeFormats.map(format => ({ val: format, text: format })));
				let selectedBarcodeFormat = localStorage.getItem('barcodeScanner-selectedBarcodeFormat');
				if (!selectedBarcodeFormat || this.barcodeFormats.findIndex(bf => bf.val === selectedBarcodeFormat) === -1) {
					selectedBarcodeFormat = 'code_39';	
				}
				this.selectedBarcodeFormat = selectedBarcodeFormat;	
				this._initCodeReader();
			});
		} else {
			this.barcodeFormats = allFormats.concat(
				Object.keys(BarcodeFormat)
					.filter(key => BarcodeFormat[BarcodeFormat[key]].toString() === key && isNaN(parseInt(key)))
					.map(key => ({ val: BarcodeFormat[key].toString(), text: key }))
			);
			let selectedBarcodeFormat = localStorage.getItem('barcodeScanner-selectedBarcodeFormat');
			if (!selectedBarcodeFormat || this.barcodeFormats.findIndex(bf => bf.val === selectedBarcodeFormat) === -1) {
				selectedBarcodeFormat =  BarcodeFormat.CODE_39.toString();	
			}
			this.selectedBarcodeFormat = selectedBarcodeFormat;	
		}
		this.isFlashSupported = false;
		this.isFlashEnabled = false;
	}

	static get properties() {
		return {
			results: { type: Array },
			devices: { type: Array },
			selectedDeviceId: { type: String },
			selectedBarcodeFormat: { type: String },
			isFlashSupported: { type: Boolean },
			isFlashEnabled: { type: Boolean },
		};
	}

	connectedCallback() {
		super.connectedCallback();
		document.addEventListener('keydown', this.__keyDown);
		document.body.classList.add('freeze-scroll');
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		document.removeEventListener('keydown', this.__keyDown);
		document.body.classList.remove('freeze-scroll');
		this._close();
	}

	firstUpdated() {
		this._initCodeReader();
	}

	render() {
		let barcode = this.getResult() || this.defaultResult;
		if (barcode != this.prevBarcode) {
			this.prevBarcode = barcode;
			this._notifyUser();
		}
		return html`
        ${colorTheme}
        ${SharedStyles}
        <style>
            :host {
                position: fixed;
                top: 0;
                left: 0;
                width: 100vw;
                height: 100vh;
                background-color: white;
                z-index: 9998;
				--barcode-musk-border: 50px;
            }
        
            .container {
                text-align: center;
                width: 100%;
                height: calc(100% - 60px);
                position: relative;
                background-color: black;
            }
        
            .toolbar {
                color: white;
                padding-right: 10px;
                align-items: center;
                height: 60px;
                background-color: var(--primary-color);
                display: flex;
                justify-content: space-between;
                fill: #fff;
            }

            .toolbar div {
                display: flex;
            }

            .toolbar div div {
                margin: 0 18px;
            }

            *[active] {
                background-color: var(--secondary-color);                     
            }

			.selectors {
				display: flex;
                justify-content: center;
                background-color: #fff;
                padding: 10px 10px 0 10px;
            }

            .selectors ddl-input {
                max-width: 300px;
			}

			.selectors ddl-input:not(:last-child) {
                margin-right: 10px;
			}

			.results {
				color: white;
				padding: 10px;
			}
			
			.video-wrapper {
                display: flex;
                justify-content: center;
                align-items: center;
                height: calc(100% - 124px);
				overflow-y: auto;
				position: relative;
            }

            .video-musk {
				border: none;
				min-height: 140px;
                max-width: 640px;
    			width: 100%;
				position: relative;
			}
			
			video {
				width: 100%;
				height: unset;
			}

            .video-musk hr {
				--hr-color-main: rgb(51, 204, 51);
				--hr-color-second: rgba(51, 204, 51, 0);
				margin: 0;
                position: absolute;
                top: calc(50% - 1px);
                left: 0;
                right: 0;
                border: 0; 
                height: 2px; 
                background-image: -webkit-linear-gradient(left, var(--hr-color-second), var(--hr-color-main), var(--hr-color-second)); 
                background-image: -moz-linear-gradient(left, var(--hr-color-second), var(--hr-color-main), var(--hr-color-second));
                background-image: -ms-linear-gradient(left, var(--hr-color-second), var(--hr-color-main), var(--hr-color-second));
				background-image: -o-linear-gradient(left, var(--hr-color-second), var(--hr-color-main), var(--hr-color-second));
				background-image: linear-gradient(to left, var(--hr-color-second), var(--hr-color-main), var(--hr-color-second));
			}

			.video-musk::after {
				content: '';
				display: block;
				position: absolute;
				top: 0;
				left: 0;
				right: 0;
				bottom: 0;
				box-shadow: 0px 0px 0px var(--barcode-musk-border) rgba(0,0,0,0.4) inset;
			}

			.video-musk::before {
				position: absolute;
				top: var(--barcode-musk-border);
				left: var(--barcode-musk-border);
				right: var(--barcode-musk-border);
				bottom: var(--barcode-musk-border);
			}

			.video-musk.flash:before {
				content: '';
				animation: flash .15s .15s alternate-reverse 1;
			}

			@keyframes flash {
				from { box-shadow: 0; }
				to { box-shadow: inset 0 0 30px 45px #fff; }
			}

			.barcode-musk {
				position: absolute;
				top: calc(var(--barcode-musk-border) + 30px);
				left: var(--barcode-musk-border);
				right: var(--barcode-musk-border);
				bottom: var(--barcode-musk-border);
				fill: rgba(255, 255, 255, 0.15);
			}
			.barcode-musk svg {
				width: 100%;
				height: 100%;
			}

			.bolder {
				font-weight: bolder;
			}
        </style>
        
        <div class="toolbar">
            <div>
                <div @click="${() => this._close()}" title="close">${close}</div>
            </div>
            <div>${this.isNativeSupported ? '[N] ' : ''}Barcode Scanner</div>
			${this.isFlashSupported ? html`
			<div>
                <div @click="${() => this._toggleFlash()}" title="Toggle Flash light">${this.isFlashEnabled ? flashOff : flashOn}</div>
            </div>
			`: ''}
            <div>
				<div @click="${() => this._openConfirmationDialog()}" title="done">${done}</div>
            </div>
        </div>
        <div class="container">
			<div class="selectors">
				<ddl-input required
					label="Video source"
					.value="${this.selectedDeviceId}"
    				.options="${this.devices}"
					validationMessage="Please select video source"
					@inspx-change="${(e) => this._deviceChanged(e)}"></ddl-input>
				<ddl-input required
					label="Barcode Format"
					.value="${this.selectedBarcodeFormat}"
    				.options="${this.barcodeFormats}"
					validationMessage="Please select barcode format"
					@inspx-change="${(e) => this._barcodeFormatChanged(e)}"></ddl-input>
			</div>
			<div class="results" @click="${() => this._notifyUser()}">
				<label>barcode: ${barcode}</label>
			</div>
			<div class="video-wrapper">
				<div class="video-musk" id="flashContainer">
					<video id="video"  width="640" height="480"></video>
					<span class="barcode-musk">${barcode_svg}</span>
					<hr>
				</div>
			</div>
        </div>`;
	}

	getResult() {
		if (this.results.length === 0) return undefined;

		const counts = this.results.reduce(function (prev, cur) {
			let v = ("" + cur).replace(/[^A-Z0-9]/ig, "_");
			prev[v] = { result: cur, count: ((prev[v] || {}).count || 0) + 1 };
			return prev;
		}, {});
		const props = Object.getOwnPropertyNames(counts);
		const max = Math.max(...props.map(prop => counts[prop].count));
		const maxProp = props.find(prop => counts[prop].count === max);
		const result = (counts[maxProp] || {}).result;
		return result;
	}

	_notifyUser() {
		this.updateComplete.then(() => {
			if (typeof navigator.vibrate === 'function') navigator.vibrate(150);
			
			let container = this.shadowRoot.getElementById('flashContainer');
			container.classList.add('flash');
			setTimeout(() => {
				container.classList.remove('flash');
			}, 500);
		});
	}

	_keyDown(e) {
		if (e.keyCode === 27) {
			this.popupOpen
				// @ts-ignore
				? this.popup.closeFunc()
				: this._close();
		}
	}

	_initCodeReader() {
		this._resetCodeReader();

		if (this.isNativeSupported) {
			if (this._allNativeFormats === undefined) return;
			const formats = (this.selectedBarcodeFormat === '-1') ? this._allNativeFormats : [this.selectedBarcodeFormat];
			this.codeReader = new NativeBarcodeScanner(formats);
		} else {
			const hints = new Map();
			const formats = [Number(this.selectedBarcodeFormat)];
			if (this.selectedBarcodeFormat !== '-1') {
				hints.set(DecodeHintType.POSSIBLE_FORMATS, formats);
			}
			this.codeReader = new BrowserMultiFormatReader(hints);
		}

		this.codeReader.listVideoInputDevices()
			.then(/** @param {MediaDeviceInfo[]} videoInputDevices */(videoInputDevices) => {
				if (!Array.isArray(videoInputDevices) || videoInputDevices.length === 0) {
					this.devices = [{ val: undefined, text: 'Nothing to select' }];
					return;
				}
				let selectedDeviceId = localStorage.getItem('barcodeScanner-selectedDeviceId');
				if (videoInputDevices.findIndex(d => (d.deviceId === selectedDeviceId)) === -1) {
					selectedDeviceId = null;
				}
				let back = videoInputDevices.find(d => (d.label || "").indexOf("back") > -1);
				if (!selectedDeviceId) {
					selectedDeviceId = back ? back.deviceId : videoInputDevices[0].deviceId;
				}
				this.selectedDeviceId = selectedDeviceId;
				this.devices = videoInputDevices.map(d => ({ val: d.deviceId, text: d.label }));
			})
			.then(() => this._startScanner())
			.catch((err) => {
				console.error(err)
			});
	}

	_resetCodeReader() {
		if (this.codeReader && typeof this.codeReader.reset === 'function') {
			this.codeReader.reset();
		}
		this.codeReader = null;
	}

	_toggleFlash() {
		if (!this.isFlashSupported) return;

		this.isFlashEnabled = !this.isFlashEnabled;

		if (this.isFlashEnabled) {
			const constraints = { advanced: [{ torch: this.isFlashEnabled }] };
			try {
				// @ts-ignore
				const stream = this.codeReader.stream;
				const track = stream.getVideoTracks()[0];
				track.applyConstraints(constraints);
			} catch { }
		} else {
			// restart video stream is only the way to turn off the flash after enabling
			this._initCodeReader();
		}
	}

	_close() {
		this._resetCodeReader();
		this.remove();
	}

	_startScanner() {
		let video = this.shadowRoot.getElementById('video');
		// @ts-ignore
		this.codeReader.decodeFromVideoDevice(this.selectedDeviceId, video, (result, err) => {
			if (result && result.getText()) {
				this.results = [...this.results, result.getText()];
			}
			
			if (err && !(err instanceof NotFoundException)) {
				console.error(err);
			}

			// @ts-ignore
			let stream = (this.codeReader || {}).stream;
			if (!this._flashCapabilitiesChecked && stream) {
				this._flashCapabilitiesChecked = true;
				try {
					const track = stream.getVideoTracks()[0];
					let capabilities = track.getCapabilities()
					this.isFlashSupported = !!(capabilities.torch);
				} catch {}
			}
		});
	}

	_openConfirmationDialog() {
		let barcode = this.getResult();

		const dialog = new InspxDialog();
		dialog.cancellable = true;
		// @ts-ignore
		dialog.content = html`Please check that the scanned value is correct: <span class="bolder">${barcode ? barcode : 'not found'}</span>`;
		dialog.addEventListener('dialog-accept', (e) => {
			this.dispatchEvent(new CustomEvent("barcode-scanned", { detail: barcode }));
			dialog.remove();
			this.popupOpen = false;
			this._close();
		});
		dialog.addEventListener('dialog-cancel', (e) => {
			this.results = [];
			this.popupOpen = false;
			dialog.remove();
		});
		document.body.appendChild(dialog);

		this.popupOpen = true;
		this.popup = dialog;
		dialog.open();
	}

	_deviceChanged(e) {
		this.results = [];
		this.selectedDeviceId = e.detail.value;
		localStorage.setItem('barcodeScanner-selectedDeviceId', this.selectedDeviceId);
		this._initCodeReader();
	}

	_barcodeFormatChanged(e) {
		this.results = [];
		this.selectedBarcodeFormat = e.detail.value;
		localStorage.setItem('barcodeScanner-selectedBarcodeFormat', this.selectedBarcodeFormat);
		this._initCodeReader();
	}
}

window.customElements.define("barcode-scanner", BarcodeScanner);
